import { usePageTitle } from "@/hooks/use-page-title";
import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useDebounce } from "@/hooks/use-debounce";
import { useInfiniteQuery, keepPreviousData } from "@tanstack/react-query";
import { useLocation, useSearch } from "wouter";
import ResponsiveLayout from "@/components/layout/responsive-layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Search, FileText, RefreshCw, Calendar, Layers, ChevronDown, ArrowUpDown, History, Loader2 } from "lucide-react";
import { StripeInvoiceHistoryModal } from "@/components/modals/stripe-invoice-history-modal";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { format, subDays, startOfWeek } from "date-fns";
import { chicagoDay } from "@/components/reports/report-date-filter";
import type { WorkOrderPayment } from "@shared/schema";

type DateRangeOption = "all" | "today" | "7days" | "30days" | "thisMonth" | "thisYear";

const dateRangeOptions: { value: DateRangeOption; label: string }[] = [
  { value: "all", label: "All Time" },
  { value: "today", label: "Today" },
  { value: "7days", label: "Last 7 Days" },
  { value: "30days", label: "Last 30 Days" },
  { value: "thisMonth", label: "This Month" },
  { value: "thisYear", label: "This Year" },
];

/**
 * First Chicago calendar day of the selected range, as YYYY-MM-DD. The server
 * cuts days at Chicago midnight, so the day must be named in Chicago too: a
 * viewer in Eastern or Pacific time would otherwise send a different date.
 */
function getDateRangeStartDay(option: DateRangeOption): string | undefined {
  const today = chicagoDay();
  switch (option) {
    case "today":
      return today;
    case "7days":
      return chicagoDay(subDays(new Date(), 7));
    case "30days":
      return chicagoDay(subDays(new Date(), 30));
    case "thisMonth":
      return `${today.slice(0, 7)}-01`;
    case "thisYear":
      return `${today.slice(0, 4)}-01-01`;
    default:
      return undefined;
  }
}

type InvoiceWithWorkOrder = WorkOrderPayment & {
  workOrder: {
    id: number;
    workOrderNumber: string;
    serviceType: string;
    companyId: number;
    companyName: string;
  };
};

const statusColors: Record<string, string> = {
  draft: "bg-gray-100 text-gray-800",
  pending: "bg-yellow-100 text-yellow-800",
  paid: "bg-green-100 text-green-800",
  partial: "bg-blue-100 text-blue-800",
  overdue: "bg-red-100 text-red-800",
  voided: "bg-gray-200 text-gray-600",
  uncollectible: "bg-red-200 text-red-800",
};

const STATUS_OPTIONS = [
  { value: "draft",          label: "Draft" },
  { value: "pending",        label: "Pending" },
  { value: "paid",           label: "Paid" },
  { value: "partial",        label: "Partial" },
  { value: "overdue",        label: "Overdue" },
  { value: "voided",         label: "Voided" },
  { value: "uncollectible",  label: "Uncollectible" },
];

const DEFAULT_STATUS_FILTER = STATUS_OPTIONS.map((s) => s.value).filter((s) => !["paid", "voided", "uncollectible"].includes(s));

interface ColumnConfig {
  id: string;
  label: string;
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
}

const COLUMN_CONFIG: ColumnConfig[] = [
  { id: 'invoiceNumber', label: 'Invoice #', defaultWidth: 120, minWidth: 80, maxWidth: 180 },
  { id: 'workOrder', label: 'Work Order', defaultWidth: 120, minWidth: 90, maxWidth: 180 },
  { id: 'company', label: 'Company', defaultWidth: 160, minWidth: 100, maxWidth: 280 },
  { id: 'status', label: 'Status', defaultWidth: 100, minWidth: 70, maxWidth: 140 },
  { id: 'batch', label: 'Batch', defaultWidth: 100, minWidth: 70, maxWidth: 140 },
  { id: 'amountDue', label: 'Amount Due', defaultWidth: 110, minWidth: 80, maxWidth: 160 },
  { id: 'amountPaid', label: 'Amount Paid', defaultWidth: 110, minWidth: 80, maxWidth: 160 },
  { id: 'balance', label: 'Balance', defaultWidth: 100, minWidth: 70, maxWidth: 150 },
  { id: 'date', label: 'Date', defaultWidth: 100, minWidth: 80, maxWidth: 150 },
  { id: 'actions', label: '', defaultWidth: 60, minWidth: 50, maxWidth: 100 },
];

const STORAGE_KEY = 'invoices-column-widths';

function getStoredColumnWidths(): Record<string, number> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return {};
}

function saveColumnWidths(widths: Record<string, number>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(widths));
  } catch {}
}

export default function InvoicesPage() {
  usePageTitle("Invoices");
  const [location, setLocation] = useLocation();
  const searchString = useSearch();
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 250);
  const [statusFilter, setStatusFilter] = useState<string[]>(() => {
    const p = new URLSearchParams(new URLSearchParams(window.location.search).toString());
    const s = p.get("status");
    if (s) { const arr = s.split(",").filter(Boolean); if (arr.length > 0) return arr; }
    return DEFAULT_STATUS_FILTER;
  });
  const [companyFilter, setCompanyFilter] = useState<string>("all");

  useEffect(() => {
    const params = new URLSearchParams(searchString);
    const status = params.get("status");
    if (status) {
      const statuses = status.split(",").filter(Boolean);
      if (statuses.length > 0) setStatusFilter(statuses);
    } else {
      setStatusFilter(DEFAULT_STATUS_FILTER);
    }
  }, [searchString]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (statusFilter.length > 0 && statusFilter.length < STATUS_OPTIONS.length) {
      params.set("status", statusFilter.join(","));
    }
    const newSearch = params.toString();
    if (newSearch !== searchString) {
      setLocation(`${location}${newSearch ? `?${newSearch}` : ''}`, { replace: true } as any);
    }
  }, [statusFilter, searchString, location, setLocation]);
  const [batchFilter, setBatchFilter] = useState<string>("all");
  const [dateRange, setDateRange] = useState<DateRangeOption>("all");
  const [sortField, setSortField] = useState<"balance" | "date">("date");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [historyInvoiceId, setHistoryInvoiceId] = useState<string | null>(null);
  const [historyLabel, setHistoryLabel] = useState<string | undefined>();
  const [historyBatchRunId, setHistoryBatchRunId] = useState<number | undefined>();

  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    const stored = getStoredColumnWidths();
    const defaults: Record<string, number> = {};
    COLUMN_CONFIG.forEach(col => {
      defaults[col.id] = stored[col.id] || col.defaultWidth;
    });
    return defaults;
  });

  const resizingRef = useRef<{ columnId: string; startX: number; startWidth: number } | null>(null);

  const handleResizeStart = useCallback((columnId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startWidth = columnWidths[columnId] || COLUMN_CONFIG.find(c => c.id === columnId)?.defaultWidth || 100;
    resizingRef.current = { columnId, startX: e.clientX, startWidth };
    
    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!resizingRef.current) return;
      const config = COLUMN_CONFIG.find(c => c.id === resizingRef.current!.columnId);
      if (!config) return;
      
      const diff = moveEvent.clientX - resizingRef.current.startX;
      const newWidth = Math.max(
        config.minWidth,
        Math.min(config.maxWidth, resizingRef.current.startWidth + diff)
      );
      
      setColumnWidths(prev => ({
        ...prev,
        [resizingRef.current!.columnId]: newWidth
      }));
    };
    
    const handleMouseUp = () => {
      if (resizingRef.current) {
        setColumnWidths(prev => {
          saveColumnWidths(prev);
          return prev;
        });
      }
      resizingRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [columnWidths]);

  const getColumnWidth = useCallback((columnId: string) => {
    return columnWidths[columnId] || COLUMN_CONFIG.find(c => c.id === columnId)?.defaultWidth || 100;
  }, [columnWidths]);

  const computedInvDateFrom = useMemo(() => {
    return getDateRangeStartDay(dateRange);
  }, [dateRange]);

  const serverStatusFilter = useMemo(() =>
    statusFilter.length > 0 && statusFilter.length < STATUS_OPTIONS.length ? statusFilter : undefined,
    [statusFilter]
  );

  const serverCompanyId = companyFilter !== 'all' ? companyFilter : undefined;
  const serverBatchMode = (batchFilter === 'batch' || batchFilter === 'single') ? batchFilter : undefined;

  const {
    data: invoicesInfinite,
    isLoading,
    refetch,
    fetchNextPage: invoicesFetchNextPage,
    hasNextPage: invoicesHasNextPage,
    isFetchingNextPage: invoicesIsFetchingNextPage,
  } = useInfiniteQuery<{ data: InvoiceWithWorkOrder[]; nextCursor: string | null; totalCount?: number }>({
    queryKey: ["/api/invoices", {
      search: debouncedSearch,
      status: serverStatusFilter,
      companyId: serverCompanyId,
      batchMode: serverBatchMode,
      dateFrom: computedInvDateFrom,
    }],
    queryFn: async ({ pageParam }) => {
      const qs = new URLSearchParams();
      if (debouncedSearch.trim()) qs.set('search', debouncedSearch.trim());
      if (serverStatusFilter) serverStatusFilter.forEach(s => qs.append('status', s));
      if (serverCompanyId) qs.set('companyId', serverCompanyId);
      if (serverBatchMode) qs.set('batchMode', serverBatchMode);
      if (computedInvDateFrom) qs.set('dateFrom', computedInvDateFrom);
      if (pageParam) qs.set('cursor', pageParam as string);
      const res = await fetch(`/api/invoices${qs.toString() ? `?${qs.toString()}` : ''}`, { credentials: 'include' });
      return res.json();
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
    refetchOnMount: "always",
  });

  const invoices = useMemo(() =>
    invoicesInfinite?.pages.flatMap(p => p.data) ?? [],
    [invoicesInfinite]
  );
  const invoicesTotalCount = invoicesInfinite?.pages[0]?.totalCount ?? invoices.length;

  const companies = useMemo(() => {
    const uniqueCompanies = new Map<number, string>();
    invoices.forEach((inv) => {
      if (inv.workOrder?.companyId && inv.workOrder?.companyName) {
        uniqueCompanies.set(inv.workOrder.companyId, inv.workOrder.companyName);
      }
    });
    return Array.from(uniqueCompanies, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [invoices]);

  const batchRuns = useMemo(() => {
    const uniqueBatches = new Set<number>();
    invoices.forEach((inv) => {
      const batchId = inv.batchInvoiceRunId;
      if (batchId !== null && batchId !== undefined) {
        uniqueBatches.add(batchId);
      }
    });
    return Array.from(uniqueBatches).sort((a, b) => b - a);
  }, [invoices]);

  const toggleSort = (field: "balance" | "date") => {
    if (sortField === field) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const toggleStatus = (value: string) => {
    setStatusFilter((prev) =>
      prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]
    );
  };

  const toggleAllStatuses = () => {
    if (statusFilter.length === STATUS_OPTIONS.length) {
      setStatusFilter([]);
    } else {
      setStatusFilter(STATUS_OPTIONS.map((s) => s.value));
    }
  };

  const statusFilterLabel = useMemo(() => {
    if (statusFilter.length === 0 || statusFilter.length === STATUS_OPTIONS.length) {
      return "All Statuses";
    }
    if (statusFilter.length === 1) {
      return STATUS_OPTIONS.find((s) => s.value === statusFilter[0])?.label ?? statusFilter[0];
    }
    if (statusFilter.length === 2) {
      return statusFilter
        .map((v) => STATUS_OPTIONS.find((s) => s.value === v)?.label ?? v)
        .join(" & ");
    }
    return `${statusFilter.length} Statuses`;
  }, [statusFilter]);

  const filteredInvoices = useMemo(() => {
    // Status, company, dateRange, batch (batch/single) are handled server-side.
    // Only specific batch run ID filter remains client-side.
    const isSpecificBatch = batchFilter !== 'all' && batchFilter !== 'batch' && batchFilter !== 'single';
    let items = invoices;
    if (isSpecificBatch) {
      items = invoices.filter((invoice) => {
        const batchId = invoice.batchInvoiceRunId;
        return batchId != null && batchId.toString() === batchFilter;
      });
    }
    return [...items].sort((a, b) => {
      let comparison = 0;
      if (sortField === "balance") {
        comparison = parseFloat(a.unpaidBalance || "0") - parseFloat(b.unpaidBalance || "0");
      } else {
        comparison = new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [invoices, batchFilter, sortField, sortDirection]);

  const summary = useMemo(() => {
    const total = filteredInvoices.reduce(
      (acc, inv) => acc + parseFloat(inv.amountDue || "0"),
      0
    );
    const paid = filteredInvoices.reduce(
      (acc, inv) => acc + parseFloat(inv.amountPaid || "0"),
      0
    );
    const outstanding = filteredInvoices.reduce(
      (acc, inv) => acc + parseFloat(inv.unpaidBalance || "0"),
      0
    );
    const paidWithClientCard = filteredInvoices.reduce(
      (acc, inv) => acc + parseFloat((inv as any).paidWithClientCardTotal || "0"),
      0
    );
    const counts = {
      total: filteredInvoices.length,
      paid: filteredInvoices.filter((i) => i.status === "paid").length,
      pending: filteredInvoices.filter((i) => i.status === "pending").length,
      draft: filteredInvoices.filter((i) => i.status === "draft").length,
    };
    return { total, paid, outstanding, paidWithClientCard, counts };
  }, [filteredInvoices]);

  return (
    <ResponsiveLayout title="Invoices" subtitle="View and manage all invoices">
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Invoices</h1>
            <p className="text-gray-500">View and manage all invoices</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="button-refresh">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Total Invoiced</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${summary.total.toFixed(2)}</div>
              <p className="text-xs text-gray-500">{summary.counts.total} invoices</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Amount Paid</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">${summary.paid.toFixed(2)}</div>
              <p className="text-xs text-gray-500">{summary.counts.paid} paid</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Outstanding</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">${summary.outstanding.toFixed(2)}</div>
              <p className="text-xs text-gray-500">{summary.counts.pending} pending</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Paid with Client Card</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-600">${summary.paidWithClientCard.toFixed(2)}</div>
              <p className="text-xs text-gray-500">Direct payments</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Drafts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-600">{summary.counts.draft}</div>
              <p className="text-xs text-gray-500">Not yet sent</p>
            </CardContent>
          </Card>
        </div>

        <Card className="overflow-hidden">
          <CardHeader>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search invoices, work orders, companies..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="input-search"
                />
              </div>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-[180px] justify-between font-normal"
                    data-testid="select-status"
                  >
                    <span className="truncate">{statusFilterLabel}</span>
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[180px] p-2" align="start">
                  <div className="space-y-1">
                    <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer text-sm font-medium">
                      <Checkbox
                        checked={statusFilter.length === STATUS_OPTIONS.length}
                        onCheckedChange={toggleAllStatuses}
                      />
                      All Statuses
                    </label>
                    <div className="border-t my-1" />
                    {STATUS_OPTIONS.map((option) => (
                      <label
                        key={option.value}
                        className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer text-sm"
                      >
                        <Checkbox
                          checked={statusFilter.includes(option.value)}
                          onCheckedChange={() => toggleStatus(option.value)}
                        />
                        {option.label}
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
              <Select value={companyFilter} onValueChange={setCompanyFilter}>
                <SelectTrigger className="w-[200px]" data-testid="select-company">
                  <SelectValue placeholder="All Companies" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Companies</SelectItem>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id.toString()}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={batchFilter} onValueChange={setBatchFilter}>
                <SelectTrigger className="w-[160px]" data-testid="select-batch">
                  <Layers className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="All Invoices" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Invoices</SelectItem>
                  <SelectItem value="batch">Batch Only</SelectItem>
                  <SelectItem value="single">Individual Only</SelectItem>
                  {batchRuns.map((batchId) => (
                    <SelectItem key={batchId} value={batchId.toString()}>
                      Batch #{batchId}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={dateRange} onValueChange={(v) => setDateRange(v as DateRangeOption)}>
                <SelectTrigger className="w-[160px]" data-testid="select-date-range">
                  <Calendar className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="All Time" />
                </SelectTrigger>
                <SelectContent>
                  {dateRangeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="text-center py-8 text-gray-500">Loading invoices...</div>
            ) : filteredInvoices.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No invoices found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table className="w-full" style={{ tableLayout: 'fixed' }}>
                  <colgroup>
                    {COLUMN_CONFIG.map(col => (
                      <col key={col.id} style={{ width: `${getColumnWidth(col.id)}px` }} />
                    ))}
                  </colgroup>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="relative group" style={{ width: getColumnWidth('invoiceNumber') }}>
                        <div className="pr-2">Invoice #</div>
                        <div 
                          className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                          onMouseDown={(e) => handleResizeStart('invoiceNumber', e)}
                        />
                      </TableHead>
                      <TableHead className="relative group" style={{ width: getColumnWidth('workOrder') }}>
                        <div className="pr-2">Work Order</div>
                        <div 
                          className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                          onMouseDown={(e) => handleResizeStart('workOrder', e)}
                        />
                      </TableHead>
                      <TableHead className="relative group" style={{ width: getColumnWidth('company') }}>
                        <div className="pr-2">Company</div>
                        <div 
                          className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                          onMouseDown={(e) => handleResizeStart('company', e)}
                        />
                      </TableHead>
                      <TableHead className="relative group" style={{ width: getColumnWidth('status') }}>
                        <div className="pr-2">Status</div>
                        <div 
                          className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                          onMouseDown={(e) => handleResizeStart('status', e)}
                        />
                      </TableHead>
                      <TableHead className="relative group" style={{ width: getColumnWidth('batch') }}>
                        <div className="pr-2">Batch</div>
                        <div 
                          className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                          onMouseDown={(e) => handleResizeStart('batch', e)}
                        />
                      </TableHead>
                      <TableHead className="relative group text-right" style={{ width: getColumnWidth('amountDue') }}>
                        <div className="pr-2">Amount Due</div>
                        <div 
                          className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                          onMouseDown={(e) => handleResizeStart('amountDue', e)}
                        />
                      </TableHead>
                      <TableHead className="relative group text-right" style={{ width: getColumnWidth('amountPaid') }}>
                        <div className="pr-2">Amount Paid</div>
                        <div 
                          className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                          onMouseDown={(e) => handleResizeStart('amountPaid', e)}
                        />
                      </TableHead>
                      <TableHead className="relative group text-right" style={{ width: getColumnWidth('balance') }}>
                        <div className="pr-2">
                          <button
                            className="inline-flex items-center gap-1 font-medium cursor-pointer bg-transparent border-0 p-0 text-inherit text-sm leading-none"
                            onClick={() => toggleSort("balance")}
                          >
                            Balance
                            <ArrowUpDown className={`h-3 w-3 ${sortField === "balance" ? "opacity-100" : "opacity-40"}`} />
                          </button>
                        </div>
                        <div 
                          className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                          onMouseDown={(e) => handleResizeStart('balance', e)}
                        />
                      </TableHead>
                      <TableHead className="relative group" style={{ width: getColumnWidth('date') }}>
                        <div className="pr-2">
                          <button
                            className="inline-flex items-center gap-1 font-medium cursor-pointer bg-transparent border-0 p-0 text-inherit text-sm leading-none"
                            onClick={() => toggleSort("date")}
                          >
                            Date
                            <ArrowUpDown className={`h-3 w-3 ${sortField === "date" ? "opacity-100" : "opacity-40"}`} />
                          </button>
                        </div>
                        <div 
                          className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                          onMouseDown={(e) => handleResizeStart('date', e)}
                        />
                      </TableHead>
                      <TableHead style={{ width: getColumnWidth('actions') }}></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredInvoices.map((invoice) => (
                      <TableRow key={invoice.id} data-testid={`row-invoice-${invoice.id}`}>
                        <TableCell className="font-medium">
                          {invoice.stripeInvoiceNumber && invoice.hostedInvoiceUrl ? (
                            <button
                              type="button"
                              onClick={() => window.open(invoice.hostedInvoiceUrl!, "_blank")}
                              className="hover:underline text-primary cursor-pointer text-left"
                              title="Open invoice in Stripe"
                              data-testid={`link-invoice-${invoice.id}`}
                            >
                              {invoice.stripeInvoiceNumber}
                            </button>
                          ) : (
                            invoice.stripeInvoiceNumber || "-"
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="link"
                            className="p-0 h-auto"
                            onClick={() => setLocation(`/work-orders/${invoice.workOrderId}`)}
                            data-testid={`link-workorder-${invoice.workOrderId}`}
                          >
                            {invoice.workOrder?.workOrderNumber || "-"}
                          </Button>
                        </TableCell>
                        <TableCell>{invoice.workOrder?.companyName || "-"}</TableCell>
                        <TableCell>
                          <Badge className={statusColors[invoice.status] || "bg-gray-100"}>
                            {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {invoice.batchInvoiceRunId !== null && invoice.batchInvoiceRunId !== undefined ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge className="gap-1 cursor-pointer bg-indigo-100 text-indigo-800 border-indigo-200 hover:bg-indigo-200">
                                    <Layers className="w-3 h-3" />
                                    #{invoice.batchInvoiceRunId}
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Part of Batch Invoice Run #{invoice.batchInvoiceRunId}. The total shown is the combined amount for all work orders in this batch.</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {invoice.batchInvoiceRunId != null ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="cursor-default border-b border-dashed border-muted-foreground/50">
                                    ${parseFloat(invoice.amountDue || "0").toFixed(2)}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Batch total — covers all work orders in Run #{invoice.batchInvoiceRunId}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            `$${parseFloat(invoice.amountDue || "0").toFixed(2)}`
                          )}
                        </TableCell>
                        <TableCell className="text-right text-green-600">
                          ${parseFloat(invoice.amountPaid || "0").toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          ${parseFloat(invoice.unpaidBalance || "0").toFixed(2)}
                        </TableCell>
                        <TableCell>
                          {invoice.createdAt
                            ? format(new Date(invoice.createdAt), "MMM d, yyyy")
                            : "-"}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {invoice.stripeInvoiceId && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setHistoryLabel(invoice.stripeInvoiceNumber
                                    ? `#${invoice.stripeInvoiceNumber}`
                                    : undefined);
                                  setHistoryBatchRunId(invoice.batchInvoiceRunId ?? undefined);
                                  setHistoryInvoiceId(invoice.stripeInvoiceId!);
                                }}
                                data-testid={`button-history-${invoice.id}`}
                                title="View Stripe event history"
                              >
                                <History className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <div className="flex flex-col items-center gap-2 mt-4 px-4 pb-4">
        <p className="text-sm text-muted-foreground" aria-live="polite">
          Showing {invoices.length} of {invoicesTotalCount}
        </p>
        {invoicesHasNextPage && (
          <Button variant="outline" onClick={() => invoicesFetchNextPage()} disabled={invoicesIsFetchingNextPage}
            aria-label="Load more invoices" aria-busy={invoicesIsFetchingNextPage}>
            {invoicesIsFetchingNextPage ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading...</>
            ) : "Load more"}
          </Button>
        )}
      </div>

      {historyInvoiceId && (
        <StripeInvoiceHistoryModal
          open={!!historyInvoiceId}
          onOpenChange={(open) => { if (!open) { setHistoryInvoiceId(null); setHistoryBatchRunId(undefined); } }}
          stripeInvoiceId={historyInvoiceId}
          label={historyLabel}
          batchRunId={historyBatchRunId}
        />
      )}
    </ResponsiveLayout>
  );
}
